<script setup lang="ts">
import type { IVirtualizedTreeNode } from "@voltalis/ui-kit/components";

import { computed, provide, ref } from "vue";
import { reactive } from "vue";
import { watch } from "vue";

import {
  IMoveSectionAppliancesWizardInject,
  MoveSectionAppliancesWizardInject,
} from "@/app/tools/new-section-management/site-sections-tree/section-appliances/move-section-appliances-wizard/move-section-appliances-wizard.types";
import { TSiteSectionsTreeData } from "@/app/tools/new-section-management/site-sections-tree/site-sections-tree.types";
import { useSiteSectionsBulkMoveAppliancesMutation } from "@/composables/kili/appliance/mutations/use-bulk-move-appliances-mutation";
import { useToaster } from "@/composables/layout/use-toaster";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { V4HSiteId } from "@/data/common/ids";
import { ProgrammableAppliance, SectionNode } from "@/domains/v4b/site-section/site-section.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import AppliancesMigrationSummaryWizardStep from "@/app/tools/new-section-management/site-sections-tree/section-appliances/move-section-appliances-wizard/appliances-migration-summary-wizard-step.vue";
import SelectTargetSectionForAppliancesWizardStep from "@/app/tools/new-section-management/site-sections-tree/section-appliances/move-section-appliances-wizard/select-target-section-for-appliances-wizard-step.vue";
import WizardTab from "@/components/ui/wizard/wizard-tab.vue";
import Wizard from "@/components/ui/wizard/wizard.vue";

interface IProps {
  siteId: V4HSiteId;
  selectedAppliances: ProgrammableAppliance[];
  initialParent: SectionNode;
  siteSectionsTree?: IVirtualizedTreeNode<TSiteSectionsTreeData>;
}

interface IEmits {
  (e: "appliances-moved"): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const visible = defineModel<boolean>("visible", {
  required: true,
});

const computedSiteId = computed<V4HSiteId>(() => props.siteId);

const { t, k } = useTypedIntl();
const toast = useToaster();

const wizardTabIndex = ref(0);

const context = reactive<IMoveSectionAppliancesWizardInject>({
  selectedAppliances: props.selectedAppliances,
  initialSelectedSection: props.initialParent,
  siteSectionsTree: props.siteSectionsTree,
  selectedTargetSection: undefined,
});

provide<IMoveSectionAppliancesWizardInject>(MoveSectionAppliancesWizardInject, context);

const { mutateAsync: bulkMoveAppliances, isPending: isBulkMoveAppliancesPending } =
  useSiteSectionsBulkMoveAppliancesMutation(computedSiteId);

async function executeAppliancesMigration(): Promise<boolean> {
  if (!context.selectedTargetSection) {
    return false;
  }

  try {
    const applianceIds = context.selectedAppliances.map((appliance) => appliance.applianceId);
    const targetSectionId = context.selectedTargetSection.id;

    await bulkMoveAppliances({
      applianceIds,
      targetSectionId,
    });

    toast.add({
      severity: "success",
      detail: t(k.tools.siteManagement.moveSiteAppliancesWizard.success),
    });

    emits("appliances-moved");

    MixpanelService.trackEvent(EMixpanelEvent.SECTION_APPLIANCE_MOVED, {
      equipment_id: applianceIds,
      from_space_id: context.initialSelectedSection.id,
      to_space_id: targetSectionId,
    });

    return true;
  } catch (e) {
    toast.add({
      severity: "error",
      detail: t(k.tools.siteManagement.moveSiteAppliancesWizard.error),
    });
    return false;
  }
}

watch(
  () => props.siteSectionsTree,
  (newTree) => {
    context.siteSectionsTree = newTree;
  },
  { immediate: true },
);

watch(
  () => props.selectedAppliances,
  (newSelection) => {
    context.selectedAppliances = newSelection;
  },
);

watch(
  () => props.initialParent,
  (newParent) => {
    context.initialSelectedSection = newParent;
  },
);
</script>

<template>
  <Wizard
    v-slot="slotProps"
    v-model:current-tab-index="wizardTabIndex"
    v-model:visible="visible"
    class="move-section-appliances-wizard w-[860px]"
    :data-test-id="DATA_TEST_IDS.components.moveSectionAppliancesWizard.component"
    :draggable="false"
    :subtitle="t(k.tools.siteManagement.moveSiteAppliancesWizard.subtitle)"
    :title="t(k.tools.siteManagement.moveSiteAppliancesWizard.title({ count: props.selectedAppliances.length }))"
  >
    <WizardTab
      :current-idx="slotProps.idx"
      :disable-next="slotProps.isNextDisabled"
      :tabs-length="slotProps.tabsLength"
    >
      <SelectTargetSectionForAppliancesWizardStep />
    </WizardTab>
    <WizardTab
      :current-idx="slotProps.idx"
      lazy
      :tabs-length="slotProps.tabsLength"
      :nextBtnLabel="t(k.tools.siteManagement.moveSiteAppliancesWizard.executeMigration)"
      :beforeFinishWizardCb="executeAppliancesMigration"
      :loading-next-btn="isBulkMoveAppliancesPending"
      :disable-previous="isBulkMoveAppliancesPending"
    >
      <AppliancesMigrationSummaryWizardStep />
    </WizardTab>
  </Wizard>
</template>

<style lang="scss">
.move-section-appliances-wizard {
  .p-dialog-content {
    @apply p-0 !important;

    .wizard {
      .wizard-tab-footer {
        @apply p-[18px] !important;
      }
    }
  }
}
</style>
